"use client";

import { useId, useMemo } from "react";
import {
  ComposedChart,
  Area,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  ReferenceArea,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { ZoomIn } from "lucide-react";
import type { YearValue } from "@/lib/data/types";
import { formatCompactCurrency, formatCompactNumber, formatPercent, formatRatio } from "@/lib/format";
import { computeNiceYAxisTicks } from "@/lib/price-chart-utils";
import { useDragZoom } from "@/lib/use-drag-zoom";

const MIN_SPAN_YEARS = 1;

// Client Components duerfen keine Funktionsprops von einer Server Component
// erhalten - daher ein simpler String-Diskriminator statt eines Formatter-Callbacks.
type ValueFormat = "ratio" | "percent" | "currency" | "number";

function formatValue(format: ValueFormat, v: number, currency: string): string {
  if (format === "percent") return formatPercent(v);
  if (format === "currency") return formatCompactCurrency(v, currency);
  if (format === "number") return formatCompactNumber(v);
  return formatRatio(v);
}

export function HistoricalLineChart({
  data,
  referenceValue,
  referenceLabel,
  format,
  currency = "USD",
  color = "#16265C",
  variant = "area",
  valueLabel = "Wert",
  disableZoom = false,
}: {
  data: YearValue[];
  referenceValue?: number;
  referenceLabel?: string;
  format: ValueFormat;
  currency?: string;
  color?: string;
  /** "bar" fuer Kennzahlen, die als diskrete Betraege je Periode Sinn ergeben (z.B. Dividende je Aktie). */
  variant?: "area" | "bar";
  /** Tooltip-Bezeichnung der Werte-Serie - Default "Wert" fuer generische Faelle, aber
   * z.B. "Persönliche Dividendenrendite" fuer die Yield-on-Cost-Hochrechnung
   * (components/dividend-projection.tsx), wo "Wert" allein zu unklar war. */
  valueLabel?: string;
  /** Deaktiviert das Klick-und-Zieh-Zoomen, Hover/Tooltip bleibt unveraendert (backlog.md
   * -> [Punkt 58]) - z.B. fuer die Dividenden-Hochrechnung, wo ein Ausschnitts-Zoom auf
   * eine reine Prognosekurve keinen echten Mehrwert bietet. Default false, damit alle
   * anderen bestehenden Aufrufer (Kursverlauf, KPI-Zeitreihen, ...) unveraendert bleiben. */
  disableZoom?: boolean;
}) {
  const gradientId = `historicalLineFill-${useId()}`;
  const barGradientId = `historicalBarFill-${useId()}`;

  // Klick+Ziehen zoomt in einen frei waehlbaren Ausschnitt (Erik 2026-07-11,
  // Feedback-Runde 8 Punkt 4: "in den Chart reingehen, klicken und zur Seite wischen"),
  // zusaetzlich zu den bestehenden 1J/3J/5J/10J/Max-Buttons in RangedHistoricalChart.
  // Filtert schlicht die schon vorhandene `data`-Prop auf den gewaehlten Jahresbereich -
  // funktioniert dadurch identisch fuer Flaechen- UND Balkendarstellung, ohne die
  // (kategoriale) X-Achse anzufassen. Mausrad-/Tastatur-Zoom (neu, Siebzehnte autonome
  // Charge) ueber denselben gemeinsamen Hook wie die uebrigen Zeitreihen-Charts - siehe
  // lib/use-drag-zoom.ts.
  const {
    zoomRange,
    resetZoom,
    refAreaLeft,
    refAreaRight,
    chartWrapperRef,
    onMouseDown,
    onMouseMove,
    onMouseUp,
    onKeyDown,
    keyboardA11yProps,
  } = useDragZoom({
    fullMin: data[0]?.year,
    fullMax: data[data.length - 1]?.year,
    minSpan: MIN_SPAN_YEARS,
    disabled: disableZoom,
  });

  const visibleData = useMemo(() => {
    if (!zoomRange) return data;
    const [lo, hi] = zoomRange;
    return data.filter((d) => d.year >= lo && d.year <= hi);
  }, [data, zoomRange]);

  // Feste Achsenbreiten schneiden lange Werte ab (z.B. "139,9 Mrd. €" bei Grossunter-
  // nehmen) - Breite stattdessen aus der laengsten tatsaechlich vorkommenden
  // formatierten Beschriftung ableiten.
  const yAxisWidth = useMemo(() => {
    const labels = visibleData.map((d) => formatValue(format, d.value, currency));
    if (referenceValue !== undefined) labels.push(formatValue(format, referenceValue, currency));
    const maxLen = labels.reduce((max, l) => Math.max(max, l.length), 0);
    return Math.max(56, Math.min(96, maxLen * 7 + 14));
  }, [visibleData, format, currency, referenceValue]);

  // Ersetzt Rechartsʼ Auto-Tick-Heuristik, die den obersten Tick teils nicht auf den
  // gleichen Rhythmus wie die uebrigen Ticks rundete (backlog.md -> Runde 5 Punkt 19).
  const yAxisTicks = useMemo(
    () => computeNiceYAxisTicks(visibleData.map((d) => d.value), variant === "bar"),
    [visibleData, variant],
  );

  if (data.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">Für diese Kennzahl liegt keine Historie vor.</p>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      {!disableZoom &&
        (zoomRange ? (
          <button
            type="button"
            onClick={resetZoom}
            className="self-start text-xs font-medium text-secondary underline underline-offset-2 hover:text-ink"
          >
            Zoom zurücksetzen ({zoomRange[0]}–{zoomRange[1]})
          </button>
        ) : (
          <span className="inline-flex items-center gap-1 self-start text-xs text-muted-foreground">
            <ZoomIn className="h-3 w-3" aria-hidden="true" />
            Bereich ziehen zum Zoomen
          </span>
        ))}
      <div
        ref={chartWrapperRef}
        className="h-56 w-full outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        onKeyDown={onKeyDown}
        {...keyboardA11yProps}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={visibleData}
            margin={{ top: 12, right: 8, left: 4, bottom: 0 }}
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
          >
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.22} />
                <stop offset="100%" stopColor={color} stopOpacity={0} />
              </linearGradient>
              {/* Balken-Modernisierung (Erik 2026-07-23: "Balkenform/Farben wirken
                  altmodisch") - dezenter Verlauf statt der vorherigen flachen
                  Halbtransparenz, gibt den Balken etwas Tiefe statt platt zu wirken. */}
              <linearGradient id={barGradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.75} />
                <stop offset="100%" stopColor={color} stopOpacity={1} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#16265C" strokeOpacity={0.14} strokeDasharray="3 5" vertical={false} />
            <XAxis
              dataKey="year"
              tick={{ fontSize: 12, fill: "#6B7280" }}
              axisLine={{ stroke: "#ECEAE3" }}
              tickLine={false}
            />
            <YAxis
              // Balken: Achse schliesst 0 IMMER mit ein (nicht nur den engen Datenbereich),
              // sonst wirken eng beieinander liegende Werte (z.B. Aktienanzahl 1,28 vs.
              // 1,32 Mrd.) optisch stark uebertrieben/irrefuehrend unterschiedlich - Erik
              // 2026-07-11: "man könnte denken es sei anders". Fuer Kennzahlen mit echten
              // negativen Jahren (z.B. Free Cashflow) bleibt die tatsaechliche Bandbreite
              // trotzdem intakt (computeNiceYAxisTicks schneidet negative Werte nicht ab).
              // Flaechen-/Linien-Charts zeigen bewusst weiterhin den engeren Trendbereich,
              // da es dort um die Richtung, nicht um absolute Balkenhoehen geht - domain und
              // ticks stammen jetzt beide aus derselben computeNiceYAxisTicks()-Berechnung
              // statt Rechartsʼ eigener Heuristik, die den obersten Tick nicht immer auf
              // denselben Rhythmus wie die uebrigen Ticks rundete.
              domain={[yAxisTicks[0], yAxisTicks[yAxisTicks.length - 1]]}
              ticks={yAxisTicks}
              tick={{ fontSize: 12, fill: "#6B7280" }}
              tickFormatter={(v: number) => formatValue(format, v, currency)}
              width={yAxisWidth}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip
              cursor={variant === "bar" ? false : { stroke: "#ECEAE3" }}
              formatter={(value) => [formatValue(format, Number(value), currency), valueLabel]}
              labelFormatter={(label) => String(label)}
              contentStyle={{
                borderRadius: 4,
                borderColor: "#ECEAE3",
                fontSize: 13,
                boxShadow: "0 4px 16px rgba(22,38,92,0.08)",
              }}
            />
            {referenceValue !== undefined && (
              <ReferenceLine
                y={referenceValue}
                stroke="#C6A15B"
                strokeDasharray="4 4"
                label={{
                  value: referenceLabel ?? formatValue(format, referenceValue, currency),
                  position: "insideTopRight",
                  fill: "#C6A15B",
                  fontSize: 11,
                }}
              />
            )}
            {variant === "bar" ? (
              <Bar
                dataKey="value"
                fill={`url(#${barGradientId})`}
                radius={[8, 8, 8, 8]}
                maxBarSize={48}
                isAnimationActive={false}
                activeBar={{ fill: color, fillOpacity: 1 }}
              />
            ) : (
              <Area
                type="linear"
                dataKey="value"
                stroke={color}
                strokeWidth={2}
                fill={`url(#${gradientId})`}
                dot={false}
                activeDot={{ r: 4, fill: color, stroke: "#FAF9F5", strokeWidth: 2 }}
                isAnimationActive={false}
              />
            )}
            {refAreaLeft !== null && refAreaRight !== null && (
              <ReferenceArea x1={refAreaLeft} x2={refAreaRight} fill="#16265C" fillOpacity={0.12} />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
